import { Progress, Table, TableColumn } from "@backstage/core-components";
import { Spacer } from "@internal/plugin-utilities";
import { Typography } from "@material-ui/core";
import React from "react";
import { VIOLATION_TABLE } from "@internal/plugin-ops-review-common";
import { TeamBreakdownHeader } from "../../TeamBreakdownHeader";
import { DatadogSloCard } from "../DatadogSloCard";

interface Props {
  data: any;
  loading: boolean;
}

export const DatadogSloSection: React.FC<Props> = ({
  data,
  loading,
}): JSX.Element => {
  if (loading) {
    return <Progress />;
  }

  const team = data.team;

  if (data.violation === true && data.violationReason === "missing_slos") {
    const tooltip = (
      <>
        <Typography variant="h5">
          {VIOLATION_TABLE.datadog.missing_slos.display}
        </Typography>
        <div>
          No SLOs were found for this entity in Datadog. These should be tagged
          with a team name that matches Radiator (ex:{" "}
          <code>team:account-ops</code>). If you're having trouble setting up
          SLOs reach out to the IPA Team.
        </div>
      </>
    );

    return (
      <div>
        <TeamBreakdownHeader
          title={VIOLATION_TABLE.datadog.missing_slos.display}
          tooltip={tooltip}
        />
        <Spacer variant="normal" />
        <Typography align="center">
          No SLOs were found for this entity. To add them create an SLO in
          Datadog and tag it with your Radiator team name (
          <code>team:{team}</code>)
        </Typography>
      </div>
    );
  }

  const columns: TableColumn<any>[] = [
    {
      title: "SLO (7d span)",
      field: "slo",
      width: "10%",
      render: (rowData) => {
        const { attributes } = rowData.data;
        return <DatadogSloCard sloHistory={rowData} key={attributes.id} />;
      },
    },
    {
      title: "Name",
      field: "name",
      render: (rowData) => {
        const { attributes } = rowData.data;
        return <Typography variant="h4">{attributes.name}</Typography>;
      },
    },
    {
      title: "Threshold",
      field: "threshold",
      render: (rowData) => {
        const { attributes } = rowData.data;
        const threshold: any = attributes.thresholds.find((t: any) => {
          return t.timeframe === "7d";
        });
        const display = threshold?.target_display || "N/A";

        return <Typography variant="h6">{display}%</Typography>;
      },
    },
    {
      title: "SLI Value",
      field: "sli_value",
      render: (rowData) => {
        const { attributes } = rowData.data;
        const overallStatus = attributes.overall_status[0];
        if (overallStatus.error) {
          return <Typography variant="h6">{overallStatus.error}</Typography>;
        }

        const precision: number = overallStatus.span_precision || 2;
        const factor = Math.pow(10, precision);
        const statusValue = Math.floor(overallStatus.status * factor) / factor;
        return <Typography variant="h6">{statusValue}%</Typography>;
      },
    },
  ];

  const jsx = (
    <Table
      options={{
        sorting: false,
        search: false,
        paging: false,
        pageSize: 20,
        padding: "dense",
      }}
      columns={columns}
      data={data.data}
    />
  );

  const tooltip = (
    <>
      <Typography variant="h5">
        {VIOLATION_TABLE.datadog.slo_below_threshold.display}
      </Typography>
      <div>
        This section indicates an SLO that has fallen below the threshold
        specified in Datadog. This allows us to reflect on changes that could be
        made to keep our statstics at expectations.
      </div>
    </>
  );

  return (
    <div>
      <TeamBreakdownHeader
        title={VIOLATION_TABLE.datadog.slo_below_threshold.display}
        tooltip={tooltip}
      />
      <Spacer variant="normal" />
      {jsx}
    </div>
  );
};
